import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Col, DatePicker, Descriptions, Form, Input, message, Modal,
  Row, Space, Statistic, Typography
} from 'antd';
import { CalculatorOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fixedAssetsApi } from './fixedAssetsApi';
import {
  findDepreciationFormErrors, friendlyErrorFor, formatRupee
} from './fixedAssetsHelpers';
import '../accounts.css';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Fixed Asset Depreciation modal (Stage 6B.9D).
//
// Operator picks from/to date → Preview → Post (Approval-MFA L1).
// Preview is a backend round-trip so the numbers exactly match what
// the server would compute (avoids any floating-point drift between
// the frontend helper and the backend `calculateDepreciation`).
// ─────────────────────────────────────────────────────────────────

export default function FixedAssetDepreciationModal({
  open, asset, onClose, onPosted, runWithApproval
}) {
  const [form] = Form.useForm();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      fromDate: dayjs().startOf('month'),
      toDate:   dayjs().endOf('month')
    });
    setPreview(null);
  }, [open, form]);

  const runPreview = useCallback(async () => {
    if (!asset) return;
    try {
      const values = await form.validateFields();
      const body = {
        fromDate: values.fromDate.format('YYYY-MM-DD'),
        toDate:   values.toDate.format('YYYY-MM-DD')
      };
      const errs = findDepreciationFormErrors(body);
      if (errs.length) { message.error(errs.join(' ')); return; }
      setBusy(true);
      const out = await fixedAssetsApi.previewDepreciation(asset.id, body);
      setPreview(out);
    } catch (e) {
      if (e && e.errorFields) return;
      message.error(friendlyErrorFor(e));
    } finally { setBusy(false); }
  }, [asset, form]);

  const runPost = useCallback(async () => {
    if (!asset || !preview) return;
    try {
      const values = await form.validateFields();
      const body = {
        fromDate: values.fromDate.format('YYYY-MM-DD'),
        toDate:   values.toDate.format('YYYY-MM-DD'),
        remarks:  values.remarks || null
      };
      const confirmed = await new Promise((resolve) => {
        let okClicked = false;
        Modal.confirm({
          title: `Post depreciation for ${asset.assetCode}?`,
          icon:  <CheckCircleOutlined style={{ color: '#3f8600' }} />,
          content: `This will post a depreciation JV through the engine for ₹ ${formatRupee(preview.depreciationAmount)}. You will be asked for a 6-digit Approval MFA code to confirm.`,
          okText: 'Post Depreciation', cancelText: 'Cancel',
          onOk:    () => { okClicked = true; },
          onCancel: () => { okClicked = false; },
          afterClose: () => resolve(okClicked)
        });
      });
      if (!confirmed) return;
      setPosting(true);
      await runWithApproval({
        operationKey:   'accounts.fixed_asset.depreciate',
        resourceType:   'fixed_asset',
        resourceId:     asset.id,
        operationLabel: 'Post asset depreciation',
        resourceLabel:  `${asset.assetCode} · ${asset.assetName}`,
        action: (approvalMfaChallengeId) =>
          fixedAssetsApi.postDepreciation(asset.id, body, { approvalMfaChallengeId })
      });
      message.success('Depreciation posted.');
      onPosted && onPosted();
    } catch (e) {
      if (e && e.name === 'ApprovalMfaCancelled') return;
      if (e && e.errorFields) return;
      message.error(friendlyErrorFor(e));
    } finally { setPosting(false); }
  }, [asset, preview, form, runWithApproval, onPosted]);

  if (!asset) return null;

  return (
    <Modal
      title={`Depreciate ${asset.assetCode} · ${asset.assetName}`}
      open={open}
      onCancel={onClose}
      width={720}
      centered
      destroyOnClose
      maskClosable={false}
      footer={
        <Space>
          <Button icon={<CalculatorOutlined />} onClick={runPreview} loading={busy}>
            Preview
          </Button>
          <Button type="primary" icon={<CheckCircleOutlined />} loading={posting}
            disabled={!preview || !(preview.depreciationAmount > 0)} onClick={runPost}>
            Post Depreciation
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
      className="erp-accounts-modal"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto', padding: '12px 16px' } }}
    >
      <Form form={form} layout="vertical" preserve className="erp-accounts-form">
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="From Date" name="fromDate"
              rules={[{ required: true, message: 'From date is required.' }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="To Date" name="toDate"
              rules={[{ required: true, message: 'To date is required.' }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Remarks" name="remarks">
          <Input.TextArea rows={2} maxLength={2000}
            placeholder="Optional — e.g. monthly depreciation Apr 2026" />
        </Form.Item>
      </Form>

      {!asset.depreciationExpenseLedgerAccountId
        || !asset.accumulatedDepreciationLedgerAccountId ? (
        <Alert type="warning" showIcon style={{ marginTop: 10 }}
          message="Depreciation ledgers not configured"
          description="Set both the Accumulated Depreciation ledger AND the Depreciation Expense ledger on this asset before posting depreciation." />
      ) : null}

      {preview ? (
        <div style={{ marginTop: 16 }}>
          <Row gutter={[10, 10]}>
            <Col xs={12} md={6}>
              <Statistic title="Opening WDV (₹)" value={preview.openingWdv} precision={2} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Depreciation (₹)" value={preview.depreciationAmount}
                precision={2} valueStyle={{ color: '#cf1322' }} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Closing WDV (₹)" value={preview.closingWdv} precision={2} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Days" value={preview.days} />
            </Col>
          </Row>
          <Descriptions size="small" column={2} bordered style={{ marginTop: 12 }}>
            <Descriptions.Item label="Method">{asset.depreciationMethod?.toUpperCase()}</Descriptions.Item>
            <Descriptions.Item label="Rate">{asset.depreciationRate != null ? `${asset.depreciationRate}%` : '—'}</Descriptions.Item>
            <Descriptions.Item label="Useful Life">{asset.usefulLifeYears != null ? `${asset.usefulLifeYears} yrs` : '—'}</Descriptions.Item>
            <Descriptions.Item label="Salvage Floor">₹ {formatRupee(preview.salvageFloor)}</Descriptions.Item>
          </Descriptions>
          {preview.depreciationAmount === 0 ? (
            <Alert type="info" showIcon style={{ marginTop: 10 }}
              message="No depreciation in this range"
              description="The computed amount is zero. Check the method / rate / useful-life on the asset, or that the salvage floor has not been reached." />
          ) : null}
        </div>
      ) : (
        <Alert type="info" showIcon style={{ marginTop: 10 }}
          message="Pick a date range and press Preview to see the computed depreciation. The JV is only posted on Post Depreciation." />
      )}
    </Modal>
  );
}
